import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { WaitlistForm } from "@/components/WaitlistForm";

interface UserTypeToggleProps {
  onToggle: (isPro: boolean) => void;
  defaultIsPro?: boolean;
}

export const UserTypeToggle = ({ onToggle, defaultIsPro }: UserTypeToggleProps) => {
  const [isPro, setIsPro] = useState(defaultIsPro ?? true);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    if (defaultIsPro !== undefined) {
      setIsPro(defaultIsPro);
    }
  }, [defaultIsPro]);

  const handleToggle = (newIsPro: boolean) => {
    setIsPro(newIsPro);
    onToggle(newIsPro);
  };

  const handleOpenWaitlist = () => {
    setShowWaitlist(true);
  };

  return (
    <div className="flex flex-col gap-2 w-full max-w-3xl mx-auto px-4">
      <div className="text-center">
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-[#F97316] mb-6">
          Vivez l'Aisne comme un local, ou presque
        </h1>
        <p className="text-lg sm:text-xl text-gray-700 mb-8">
          Vous savez ce qui est frustrant ? Être perdu dans l'Aisne alors que vous avez l'impression que tout le monde, même les vaches, connaît les meilleurs coins. Heureusement, NLocale est là pour vous sauver la mise, que vous soyez touriste ou résident depuis toujours.
        </p>
        
        <button 
          onClick={handleOpenWaitlist}
          className="bg-[#F97316] text-white px-6 py-3 rounded-lg hover:bg-[#F97316]/90 transition-colors mb-12"
        >
          Rejoignez le mouvement !
        </button>

        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-8">
          Locaux, Visiteurs ou Rêveurs : L'Aisne a quelque chose à offrir à chacun !
        </h2>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <h2 className="text-2xl font-bold text-primary mb-4">Pour les Résidents</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Parce que oui, même si vous vivez ici depuis des lustres, il y a encore des endroits secrets que vos grands-parents ne vous ont jamais révélés. On vous les montre avant que votre voisin ne les découvre !
              </p>
              <p className="text-gray-700">
                Finis les dîners improvisés où personne ne sait où aller. Avec NLocale, vous serez l'expert ou l'experte du quartier (et peut-être même du département) !
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <h2 className="text-2xl font-bold text-primary mb-4">Pour les Touristes</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Pas besoin d'être né·e dans l'Aisne pour avoir l'air d'un pro. Grâce à NLocale, vous pourrez dire "Ah oui, c'est mon bistro préféré" dès votre première journée ici.
              </p>
              <p className="text-gray-700">
                Et non, vous n'aurez pas à manger du fromage au petit-déjeuner juste parce que vous êtes en France. On a mieux à vous proposer !
              </p>
            </div>
          </div>

          <div className="md:col-span-2 lg:col-span-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <h2 className="text-2xl font-bold text-primary mb-4">Pour Tous</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Que vous aimiez les soirées festives, les balades bucoliques ou simplement les bonnes tartines du dimanche, on a ce qu'il vous faut. Parce que chez NLocale, on croit que l'Aisne mérite bien plus que ses clichés.
              </p>
            </div>
          </div>
        </div>
      </div>

      <WaitlistForm 
        isPro={false}
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
      />
    </div>
  );
};
